// Project + tab resolution for the multi-project SPA.
//
// The URL is the single source of truth: `/p/{id}/{tab}`. The project id and
// the active tab both live in the path (react-router drives navigation, no
// reloads). Several projects can stay open at once, one per browser tab.
// There is no server-side "active project"; a URL with no `/p/{id}` is the
// dashboard.
//
// Every API call carries the id as the `X-Project-Id` header (see
// `api/client.ts`). The SSE EventSource carries it as a `?project_id=`
// query param instead, because the native EventSource API can't set
// custom headers (see `hooks/useSSE.ts`).

import type { TabId } from '../types';

export const VALID_TABS: TabId[] = [
  'pipeline', 'extraction', 'annotation', 'review', 'boq', 'research', 'prompts', 'upload', 'cables',
];
export const OWNER_ONLY_TABS: TabId[] = ['pipeline', 'prompts', 'upload', 'cables'];
export const DEFAULT_TAB_OWNER: TabId = 'pipeline';
export const DEFAULT_TAB_ANNOTATOR: TabId = 'extraction';

/** Extract the project id from a URL path, or null if not a `/p/{id}` route. */
export function projectIdFromPath(
  pathname: string = window.location.pathname,
): string | null {
  const m = pathname.match(/^\/p\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Extract the tab from a `/p/{id}/{tab}` path, or null. */
export function tabFromPath(
  pathname: string = window.location.pathname,
): TabId | null {
  const m = pathname.match(/^\/p\/[^/?#]+\/([^/?#]+)/);
  const tab = m ? decodeURIComponent(m[1]) : null;
  return tab && (VALID_TABS as string[]).includes(tab) ? (tab as TabId) : null;
}

/** Build the canonical URL for a project tab. */
export function projectPath(projectId: string, tab: TabId): string {
  return `/p/${encodeURIComponent(projectId)}/${tab}`;
}

/** The project id for the current URL, or null (dashboard). */
export function getActiveProjectId(): string | null {
  return projectIdFromPath();
}

/**
 * Append `?project_id=` to a URL when a project is active.
 *
 * For URLs consumed where headers can't be set — `<img src>`, PDF/iframe
 * src, `new Image().src` — the middleware reads `?project_id=` as an
 * equivalent to the `X-Project-Id` header. No-op when no project is active.
 */
export function withProjectQuery(url: string): string {
  const pid = getActiveProjectId();
  if (!pid) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}project_id=${encodeURIComponent(pid)}`;
}
